import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Spinner, PageSkeleton } from '@daycal/ui';
import { api } from '../lib/apiClient.js';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

export default function Availability() {
  const { data, isLoading } = useQuery({ queryKey: ['availability'], queryFn: api.getAvailability });
  const [rows, setRows] = useState([]);
  const [initialRows, setInitialRows] = useState([]);
  const [tz, setTz] = useState('Asia/Kolkata');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!data) return;
    setTz(data.schedule?.timezone || 'Asia/Kolkata');
    const byDay = new Map((data.rules || []).map((r) => [r.day_of_week, r]));
    const initial = DAYS.map((_d, i) => {
        const r = byDay.get(i);
        const existingBreaks = (r?.breaks || []).filter(b => b.break_start && b.break_end);
        return {
          day_of_week: i,
          enabled: r?.enabled ?? (i >= 1 && i <= 5),
          start_time: hhmm(r?.start_time) || '09:00',
          end_time: hhmm(r?.end_time) || '17:00',
          has_break: existingBreaks.length > 0,
          break_start: existingBreaks.length > 0 ? hhmm(existingBreaks[0].break_start) : '12:00',
          break_end: existingBreaks.length > 0 ? hhmm(existingBreaks[0].break_end) : '13:00',
        };
      });
    setRows(initial);
    setInitialRows(initial);
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.updateAvailability({
      timezone: tz,
      rules: rows.map((r) => ({
        day_of_week: r.day_of_week,
        enabled: r.enabled,
        start_time: r.start_time,
        end_time: r.end_time,
        breaks: r.has_break && r.enabled
          ? [{ break_start: r.break_start, break_end: r.break_end }]
          : [],
      })),
    }),
    onSuccess: () => {
      setMsg('Saved');
      setInitialRows(rows);
      setTimeout(() => setMsg(''), 3000);
    },
    onError: (err) => setMsg(err.message),
  });

  const isDirty = JSON.stringify(rows) !== JSON.stringify(initialRows);

  if (isLoading) return <PageSkeleton />;

  const update = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const Checkbox = ({ checked, onChange, size = 'md' }) => {
    const sz = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
    const iconSz = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';
    return (
      <div
        className={`grid ${sz} place-items-center rounded border transition-colors cursor-pointer ${
          checked ? 'bg-slate-900 border-slate-900 text-white' : 'border-slate-300 bg-white hover:border-slate-400'
        }`}
        onClick={() => onChange(!checked)}
      >
        {checked && (
          <svg className={iconSz} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
    );
  };

  return (
    <div className="animate-dc-rise mx-auto max-w-4xl">
      <div className="rounded-3xl bg-white p-5 md:p-6 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Weekly Availability</h1>
            <p className="mt-1 text-sm text-slate-500">Set your working hours for each day to prevent double bookings.</p>
          </div>
          <div className="flex items-center gap-3">
             {msg && <span className="text-sm font-medium text-emerald-600">{msg}</span>}
             <button onClick={() => save.mutate()} disabled={!isDirty || save.isPending} className="flex items-center justify-center rounded-full bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed">
               {save.isPending ? <Spinner /> : 'Update Availability'}
             </button>
          </div>
        </div>
        
        <div className="mt-1 flex flex-col gap-1.5">
          {rows.map((r, i) => (
            <div key={r.day_of_week} className={`flex flex-col gap-1.5 rounded-xl px-4 py-2 transition-colors border ${r.enabled ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-50 border-slate-100 opacity-60 grayscale-[0.5]'}`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                {/* Left Side: Checkbox and Day */}
                <div className="flex items-center gap-2 w-32 shrink-0">
                  <Checkbox checked={r.enabled} onChange={(v) => update(i, { enabled: v })} />
                  <span className={`text-sm font-bold ${r.enabled ? 'text-slate-900' : 'text-slate-500'}`}>{DAYS[r.day_of_week]}</span>
                </div>
                
                {/* Right Side: Times */}
                <div className={`flex items-center gap-2 transition-opacity ${r.enabled ? 'opacity-100' : 'opacity-40'}`}>
                  <input
                    type="time"
                    value={r.start_time}
                    disabled={!r.enabled}
                    onChange={(e) => update(i, { start_time: e.target.value })}
                    className="h-8 w-32 rounded-full border border-slate-400 bg-white px-3 text-xs font-semibold text-slate-700 outline-none focus:border-slate-300 focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50 disabled:text-slate-400 cursor-pointer"
                  />
                  <span className="text-xs font-medium text-slate-400">to</span>
                  <input
                    type="time"
                    value={r.end_time}
                    disabled={!r.enabled}
                    onChange={(e) => update(i, { end_time: e.target.value })}
                    className="h-8 w-32 rounded-full border border-slate-400 bg-white px-3 text-xs font-semibold text-slate-700 outline-none focus:border-slate-300 focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50 disabled:text-slate-400 cursor-pointer"
                  />
                </div>
              </div>

              {/* Break / Lunch Time */}
              {r.enabled && (
                <div className={`ml-[42px] flex flex-wrap items-center gap-3 mt-1.5 md:mt-0 rounded-lg px-2.5 py-1.5 transition-colors ${r.has_break ? 'bg-red-50/70 border border-red-100' : 'bg-transparent'}`}>
                  <label className="flex items-center gap-2 cursor-pointer group" onClick={(e) => e.preventDefault()}>
                    <Checkbox
                      size="sm"
                      checked={r.has_break}
                      onChange={(v) => update(i, { has_break: v })}
                    />
                    <span className={`text-xs font-bold transition-colors ${r.has_break ? 'text-red-700' : 'text-slate-500 group-hover:text-slate-700'}`}>
                      Add Break / Lunch Time
                    </span>
                  </label>

                  {r.has_break && (
                    <div className="flex items-center gap-2 animate-dc-rise">
                      <span className="text-xs font-bold text-red-400">from</span>
                      <input
                        type="time"
                        value={r.break_start}
                        onChange={(e) => update(i, { break_start: e.target.value })}
                        className="h-7 w-28 rounded-full border border-red-200 bg-white px-2.5 text-xs font-bold text-red-700 outline-none focus:border-red-300 focus:ring-2 focus:ring-red-100 cursor-pointer shadow-sm"
                      />
                      <span className="text-xs font-bold text-red-400">to</span>
                      <input
                        type="time"
                        value={r.break_end}
                        onChange={(e) => update(i, { break_end: e.target.value })}
                        className="h-7 w-28 rounded-full border border-red-200 bg-white px-2.5 text-xs font-bold text-red-700 outline-none focus:border-red-300 focus:ring-2 focus:ring-red-100 cursor-pointer shadow-sm"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        
      </div>
    </div>
  );
}
